import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../cmsConfig';
import {
  BillingViewModel,
  getInvoiceStatusLabel,
  getPartialInvoiceLabel,
  InvoiceDocumentListItem,
} from '../../types/Billing';

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: cmsApiConfig.url,
    headers: {
      'X-CSRF': '1',
    },
    credentials: 'include',
  }),
  tagTypes: ['InvoiceDocuments'],
  endpoints: (builder) => ({
    getPageTexts: builder.query<BillingViewModel, void>({
      query: () => ({
        url: '/billing/getPageTexts',
        method: 'GET',
      }),
    }),
    getInvoiceDocuments: builder.query<InvoiceDocumentListItem[], void>({
      query: () => ({
        url: `/billing/getInvoiceDocuments`,
        method: 'GET',
      }),
      providesTags: ['InvoiceDocuments'],
      transformResponse: (response: InvoiceDocumentListItem[]) => {
        return response.map((invoiceDocument) => {
          const invoiceItemsWithStatusLabels = invoiceDocument.invoiceItems.map(
            (invoiceItem) => {
              return {
                ...invoiceItem,
                partialInvoiceLabel: getPartialInvoiceLabel(
                  invoiceItem.partialInvoice
                ),
              };
            }
          );
          if (invoiceDocument.status) {
            return {
              ...invoiceDocument,
              statusLabel: getInvoiceStatusLabel(invoiceDocument.status),
              invoiceItems: invoiceItemsWithStatusLabels,
            };
          }
          return {
            ...invoiceDocument,
            invoiceItems: invoiceItemsWithStatusLabels,
          };
        });
      },
    }),
    getInvoiceDocumentsByRefNumber: builder.query<
      InvoiceDocumentListItem[],
      string
    >({
      query: (refNumber) => ({
        url: `/billing/getInvoiceDocumentsByRefNumber?refNumber=${refNumber}`,
        method: 'GET',
      }),
    }),
    ApproveInvoiceDocument: builder.mutation<void, string>({
      query: (refNumber) => ({
        url: `/billing/approveInvoiceDocument?refNumber=${refNumber}`,
        method: 'POST',
      }),
      invalidatesTags: ['InvoiceDocuments'],
    }),
    GetInvoiceDocumentAsPdf: builder.query<Blob, string>({
      query: (refNumber) => ({
        url: `/billing/getInvoiceDocumentAsPdf?refNumber=${refNumber}`,
        method: 'GET',
        responseHandler(response) {
          return response.blob();
        },
      }),
    }),
    GetInvoiceDocumentAsCsv: builder.query<Blob, InvoiceDocumentListItem>({
      query: (data) => {
        const transformedData = {
          ...data,
          invoiceItems: data.invoiceItems.map((item) => ({
            ...item,
            // Keep both keys for compatibility across serializer/configuration differences.
            DeltagarID: item.participantId,
          })),
        };

        return {
          url: '/billing/getInvoiceDocumentAsCsv',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(transformedData),
          responseHandler(response) {
            return response.blob();
          },
        };
      },
    }),
    GetApprovedInvoiceDocumentAsPdf: builder.query<
      Blob,
      { refNumber: string; invoiceNumber: string }
    >({
      query: ({ refNumber, invoiceNumber }) => ({
        url: `/billing/GetApprovedInvoiceAsPdf?refNumber=${refNumber}&invoiceNumber=${invoiceNumber}`,
        method: 'GET',
        responseHandler(response) {
          return response.blob();
        },
      }),
    }),
  }),
});

export const {
  useLazyGetPageTextsQuery,
  useApproveInvoiceDocumentMutation,
  useLazyGetInvoiceDocumentsByRefNumberQuery,
  useLazyGetInvoiceDocumentsQuery,
  useLazyGetInvoiceDocumentAsPdfQuery,
  useLazyGetInvoiceDocumentAsCsvQuery,
  useLazyGetApprovedInvoiceDocumentAsPdfQuery,
} = billingApi;
